import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X, LucideIcon } from 'lucide-react'

import { Button } from './Button'
import { IconButton } from './IconButton'

/**
 * LA MODALE UNIQUE (LOT-H7).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La recette DA a mesuré, sur cinq modales seulement (DA-018) :
 *
 *   - **trois largeurs** — 500, 672 et 700 px, sans qu'aucune classe de contenu
 *     ne les distingue ;
 *   - **trois en-têtes** — tuile d'icône + titre SANS croix (campagne), tuile +
 *     titre + croix (utilisateur), titre + sous-titre + croix (client) ;
 *   - **deux calques d'overlay** — `z-index: 50` sur la modale client, qui
 *     laisse la barre du haut de l'`AppShell` (`z-[100]`) **hors du voile**,
 *     contre `z-index: 1000` sur les deux autres.
 *
 * Et, sur les mêmes cinq (DA-006) : `[role="dialog"]` → **0**, `[aria-modal]` →
 * **0**, aucun piège de focus, et sur deux des trois testées le focus **ne
 * rentre jamais** — il reste sur le bouton déclencheur, derrière le voile.
 *
 * Ce composant est la source unique. Une modale écrite à la main est une
 * régression, quelle qu'en soit la raison.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE PATRON VIENT DE `telephony/` — EN LECTURE SEULE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `telephony/scene/ScriptOverlay.tsx` porte déjà le bon geste (portail sur
 * `document.body`, `role="dialog"` + `aria-modal`, Échap en phase de CAPTURE,
 * `touchAction: 'none'` sur le voile). Il a été lu, pas importé : `telephony/**`
 * est une **zone gelée** (design system §9) et ne doit porter aucune dépendance
 * inverse vers le design system, sinon le gel ne veut plus rien dire et la
 * bascule v2 traîne un fichier de plus.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * TROIS LARGEURS, ET ELLES SONT NOMMÉES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La largeur suit la **classe de contenu**, pas l'humeur de l'écran :
 *
 * | Largeur | Cote   | Emploi                                                  |
 * |---------|--------|---------------------------------------------------------|
 * | `sm`    | 420 px | Confirmation, message court, choix binaire              |
 * | `md`    | 560 px | Formulaire courant (1 colonne)                          |
 * | `lg`    | 700 px | Formulaire long ou à 2 colonnes, tableau, prévisualisation |
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE PIED EST TOUJOURS VISIBLE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La modale est une **colonne** bornée à `90vh` : l'en-tête et le pied ne
 * défilent pas (`shrink-0`), seul le corps défile. Avant, la carte entière
 * défilait et « Annuler / Créer » sortait de l'écran dès 820 px de haut — la
 * hauteur d'un iPad en paysage, c'est-à-dire la cible du produit.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * UNE SAISIE MODIFIÉE NE SE PERD PAS D'UN TOUCHER (#262)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Sur iPad, le clavier virtuel déplace la boîte : le toucher qui devait
 * fermer le clavier tombe sur le voile, et une fiche à moitié saisie
 * disparaissait. Un formulaire passe `dirty` dès que sa saisie diffère de
 * l'état d'ouverture (`utils/formDirty.ts`) : les trois sorties PROPRES à la
 * modale (voile, Échap, croix) demandent alors « Abandonner les
 * modifications ? » à la place du pied — rien ne se ferme sans « Abandonner ».
 * Pendant la question, Échap reprend la saisie et le voile ne fait rien.
 * Le bouton « Annuler » du pied appartient à l'appelant : intention explicite,
 * il ferme sans question.
 */

export type ModalWidth = 'sm' | 'md' | 'lg'

const WIDTHS: Record<ModalWidth, string> = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[700px]',
}

/**
 * PLACEMENT (MESSAGERIE-UX, #502 🟠19).
 *
 * `center` (défaut) : la boîte centrée, bornée à `90vh` — la modale de H7.
 * `side` : un VOLET ancré au bord droit, pleine hauteur, largeur unique de
 * 480 px (`ui/Drawer`). Le voile, le portail, `role="dialog"`, le focus
 * piégé puis rendu, Échap en capture et la fermeture au voile sont les MÊMES :
 * un volet écrit à la main (l'ancien `SmsDetailDrawer` : pas de rôle, Échap
 * muet, focus sur `body`) est une régression au même titre qu'une modale.
 */
export type ModalPlacement = 'center' | 'side'

const OVERLAY_BY_PLACEMENT: Record<ModalPlacement, string> = {
  center: 'items-center justify-center p-4',
  side: 'items-stretch justify-end p-0',
}

const BOX_BY_PLACEMENT: Record<ModalPlacement, string> = {
  center: 'max-h-[90vh] rounded-card border',
  side: 'h-full max-h-full max-w-[480px] rounded-none border-l',
}

/**
 * UN SEUL CALQUE, AU-DESSUS DE TOUT CE QUI EST APPLICATIF.
 *
 * `9990` passe au-dessus du châssis (`AppShell` : `z-[100]`, qui crée son propre
 * contexte d'empilement et confine donc le `z-[9999]` de son menu utilisateur) —
 * c'est précisément ce que la modale client à `z-50` ratait, en laissant la barre
 * du haut à découvert.
 *
 * Et il reste **en dessous** de deux familles que ce lot n'ouvre pas : les toasts
 * (`ToastProvider`, `99999` — H9) et les surfaces de téléphonie v2
 * (`WrapupEscape`, `10000`). Une bulle de notification doit rester lisible
 * par-dessus une modale ; une sortie de secours d'appel en cours ne se laisse
 * jamais recouvrir par un formulaire.
 */
export const MODAL_Z_INDEX = 9990

export interface ModalProps {
  /** La modale ne rend rien tant que `open` est faux — pas de démontage à gérer côté appelant. */
  open: boolean
  onClose: () => void
  /** Titre, cran T2. Nomme la boîte de dialogue (`aria-labelledby`). */
  title: string
  /** Ligne secondaire sous le titre (nom du client, de l'élément visé…). */
  subtitle?: ReactNode
  /**
   * Icône d'en-tête. Sa tuile est **neutre** — jamais teintée (DA-048) : le
   * `PageHeader` de H2 a imposé la tuile neutre, les modales ne suivaient pas.
   */
  icon?: LucideIcon
  width?: ModalWidth
  /** `center` (défaut) ou `side` (volet ancré à droite — passer par `ui/Drawer`). */
  placement?: ModalPlacement
  /**
   * Pied de modale. TOUJOURS visible : il ne défile pas avec le corps.
   * Convention d'appel : `<Button className="flex-1">` × 2, annulation à gauche.
   */
  footer?: ReactNode
  /**
   * Quand elle est fournie, le corps ET le pied sont rendus dans un `<form>` :
   * un bouton `type="submit"` posé dans le pied soumet bien, malgré la coupure
   * visuelle entre les deux zones.
   */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
  /**
   * Opération en cours. Neutralise les trois sorties (croix, Échap, clic sur le
   * voile) : une soumission ne se ferme pas sous les doigts. La croix reste
   * RENDUE — elle est systématique (DA-018) — mais désactivée.
   */
  busy?: boolean
  /**
   * Saisie modifiée depuis l'ouverture (#262). Le voile, Échap et la croix
   * demandent confirmation au lieu de fermer.
   */
  dirty?: boolean
  /** Libellé de la croix. Par défaut `common:actions.close`. */
  closeLabel?: string
  children: ReactNode
  'data-testid'?: string
}

/**
 * Sélecteur des éléments qui peuvent recevoir le focus au clavier. Sert deux
 * fois : au montage pour placer le focus, puis à chaque Tab pour le piéger.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Champs de saisie — la cible privilégiée du focus d'ouverture. */
const FIRST_FIELD = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled])'

const isVisible = (element: HTMLElement) =>
  !element.hasAttribute('hidden') &&
  element.getAttribute('aria-hidden') !== 'true' &&
  element.tabIndex !== -1

/**
 * PILE DES MODALES OUVERTES (#287).
 *
 * Chaque `Modal` installe ses écouteurs Tab/Échap sur `window` en capture, sans
 * notion de pile : deux modales ouvertes ensemble se battaient (Échap fermait
 * les deux, Tab renvoyait le focus de la modale du dessus vers celle du
 * dessous). Le registre est un simple tableau de module : seule la modale du
 * DESSUS (la dernière ouverte) réagit à Échap et à Tab ; à sa fermeture, le
 * focus retourne là d'où elle a été ouverte, ou — quand cette origine n'existe
 * plus ou n'a jamais eu le focus (Safari ne focalise pas un bouton cliqué) — sur
 * la boîte de la modale qui redevient le dessus. Une modale seule se comporte
 * exactement comme avant.
 */
interface StackedModal {
  dialog: () => HTMLElement | null
}
const modalStack: StackedModal[] = []

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  width = 'md',
  placement = 'center',
  footer,
  onSubmit,
  busy = false,
  dirty = false,
  closeLabel,
  children,
  'data-testid': testId,
}: ModalProps) {
  const { t } = useTranslation('common')
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const subtitleId = useId()

  /**
   * `busy` est relu dans des écouteurs natifs installés une seule fois. Une
   * `ref` évite de les réinstaller à chaque frappe — et évite surtout qu'un
   * écouteur capturé lise un `busy` périmé au moment d'Échap.
   */
  const busyRef = useRef(busy)
  busyRef.current = busy

  const closeRef = useRef(onClose)
  closeRef.current = onClose

  /** Cette modale dans la pile (#287) — renseigné pendant qu'elle est ouverte. */
  const entryRef = useRef<StackedModal | null>(null)
  const isTopModal = () => entryRef.current !== null && modalStack[modalStack.length - 1] === entryRef.current

  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty

  /** Question « Abandonner les modifications ? » affichée (#262). */
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const confirmingRef = useRef(confirmingDiscard)
  confirmingRef.current = confirmingDiscard
  const keepEditingRef = useRef<HTMLButtonElement>(null)

  // Fermée (ou saisie revenue à l'état d'ouverture) : plus de question en suspens.
  useEffect(() => {
    if (!open || !dirty) setConfirmingDiscard(false)
  }, [open, dirty])

  // La question prend le focus sur le choix SANS perte.
  useEffect(() => {
    if (confirmingDiscard) keepEditingRef.current?.focus()
  }, [confirmingDiscard])

  /** Les trois sorties propres à la modale : voile, Échap, croix. */
  const requestClose = useCallback(() => {
    if (busyRef.current) return
    if (dirtyRef.current) {
      // Déjà posée : un nouveau toucher à côté ne vaut pas « oui ».
      setConfirmingDiscard(true)
      return
    }
    closeRef.current()
  }, [])

  /**
   * FOCUS D'OUVERTURE — le défaut le plus concret de DA-006 : sur deux modales
   * sur trois, le focus restait sur le bouton déclencheur, DERRIÈRE le voile.
   * Un utilisateur au clavier tabulait alors dans la page masquée.
   *
   * Ordre de préférence : premier CHAMP (l'utilisateur ouvre un formulaire pour
   * le remplir), sinon premier élément focusable, sinon la boîte elle-même —
   * qui porte `tabIndex={-1}` exprès pour pouvoir recevoir ce focus de repli.
   */
  useEffect(() => {
    if (!open) return
    const dialog = dialogRef.current
    if (!dialog) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    const entry: StackedModal = { dialog: () => dialogRef.current }
    entryRef.current = entry
    modalStack.push(entry)

    const field = Array.from(dialog.querySelectorAll<HTMLElement>(FIRST_FIELD)).find(isVisible)
    const fallback = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).find(isVisible)
    ;(field ?? fallback ?? dialog).focus()

    // Le focus revient d'où il venait à la fermeture : sans cela, il retombe sur
    // <body> et la tabulation suivante repart du haut de la page.
    return () => {
      const index = modalStack.indexOf(entry)
      if (index !== -1) modalStack.splice(index, 1)
      entryRef.current = null
      if (previouslyFocused && previouslyFocused !== document.body && previouslyFocused.isConnected) {
        previouslyFocused.focus?.()
        return
      }
      // Origine sans focus (Safari) ou disparue : si une autre modale redevient
      // le dessus, elle reprend le focus plutôt que <body>.
      modalStack[modalStack.length - 1]?.dialog()?.focus()
    }
  }, [open])

  /**
   * ÉCHAP — en phase de CAPTURE, et la propagation est coupée.
   *
   * Échap est aussi le raccourci « raccrocher » de la scène de téléphonie
   * (`useSceneShortcuts`, écouteur sur `window` en phase de bulle). Fermer une
   * modale ne doit jamais couper une conversation : on passe avant lui, et il ne
   * voit pas l'événement.
   */
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Pile (#287) : seule la modale du dessus répond.
      if (!isTopModal()) return
      event.preventDefault()
      event.stopPropagation()
      // Échap pendant la question = « je n'ai pas voulu fermer ».
      if (confirmingRef.current) {
        setConfirmingDiscard(false)
        return
      }
      requestClose()
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [open, requestClose])

  /**
   * PIÈGE DE FOCUS — Tab et Maj+Tab bouclent à l'intérieur de la boîte.
   *
   * Sans lui, `aria-modal` ment : la boîte se DÉCLARE modale et le clavier en
   * sort quand même, vers une page qui n'est plus visible sous le voile.
   */
  useEffect(() => {
    if (!open) return

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      if (!isTopModal()) return
      const dialog = dialogRef.current
      if (!dialog) return

      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible)
      if (focusables.length === 0) {
        // Aucune cible : le focus reste sur la boîte plutôt que de fuir.
        event.preventDefault()
        dialog.focus()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (!active || !dialog.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
        return
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleTab, true)
    return () => window.removeEventListener('keydown', handleTab, true)
  }, [open])

  /**
   * Le fond ne défile pas pendant qu'une modale est ouverte. Sur iPad, sans
   * cela, le geste de défilement du corps se propage à la page et l'utilisateur
   * retrouve un écran déplacé en refermant.
   */
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  if (!open) return null

  const body = (
    <>
      {/* `overscroll-contain` : arrivé en bout de corps, le geste ne se propage
          pas à la page (chaînage de défilement iOS). */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">
        {children}
      </div>
      {confirmingDiscard ? (
        /* À la place du pied : la question reste visible (pied fixe), au plus
           près du pouce, et le pied de l'appelant ne peut pas soumettre
           pendant qu'elle est posée. */
        <div data-testid="modal-discard-confirm" className="shrink-0 border-t border-border px-5 py-4">
          <p role="alert" className="text-t4 font-medium text-foreground">
            {t('modal.discard.question')}
          </p>
          <p className="mt-0.5 text-t5 text-foreground-secondary">{t('modal.discard.hint')}</p>
          <div className="mt-3 flex items-center gap-3">
            <Button
              ref={keepEditingRef}
              variant="secondary"
              className="flex-1"
              onClick={() => setConfirmingDiscard(false)}
            >
              {t('modal.discard.keep')}
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => {
                setConfirmingDiscard(false)
                closeRef.current()
              }}
            >
              {t('modal.discard.confirm')}
            </Button>
          </div>
        </div>
      ) : (
        footer && (
          <div className="flex shrink-0 items-center gap-3 border-t border-border px-5 py-4">
            {footer}
          </div>
        )
      )}
    </>
  )

  return createPortal(
    <div
      role="presentation"
      data-testid="modal-overlay"
      /**
       * Fermeture au `mousedown` et NON au `click` : un clic dont l'appui
       * commence dans la boîte (sélection de texte glissée jusqu'au bord) et se
       * relâche sur le voile refermait la modale en pleine saisie.
       *
       * `touchAction: 'none'` — même recette que `ScriptOverlay` : sans lui, un
       * glissement sur le voile fait défiler la PAGE derrière la modale.
       */
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return
        // `preventDefault` : l'action par défaut d'un appui sur le voile (non
        // focusable) envoie le focus sur <body> APRÈS ce gestionnaire — elle
        // écrasait le focus posé sur « Continuer la saisie » (#262, banc).
        event.preventDefault()
        requestClose()
      }}
      style={{ touchAction: 'none', zIndex: MODAL_Z_INDEX }}
      data-placement={placement}
      className={`fixed inset-0 flex bg-black/70 backdrop-blur-sm ${OVERLAY_BY_PLACEMENT[placement]}`}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        data-testid={testId}
        className={[
          'flex w-full flex-col overflow-hidden',
          'border-border bg-background-primary shadow-2xl focus:outline-none',
          BOX_BY_PLACEMENT[placement],
          placement === 'center' ? WIDTHS[width] : '',
        ].join(' ')}
      >
        {/* UN SEUL EN-TÊTE : tuile neutre optionnelle, titre T2, croix
            systématique. Les trois en-têtes relevés par DA-018 se ramènent à
            celui-ci — y compris celui qui n'avait pas de croix du tout. */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {Icon && (
              /* Tuile NEUTRE (DA-048). Les tuiles de section colorées — bleu,
                 vert, dégradé violet — ne portaient aucun rôle métier : le §1.2
                 interdit la couleur décorative. */
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-background-secondary text-foreground-secondary">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
            )}
            <div className="min-w-0">
              <h2 id={titleId} className="truncate text-t2 text-foreground">
                {title}
              </h2>
              {subtitle && (
                <p id={subtitleId} className="mt-0.5 truncate text-t5 text-foreground-secondary">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <IconButton
            icon={X}
            label={closeLabel ?? t('actions.close')}
            onClick={requestClose}
            disabled={busy}
          />
        </div>

        {onSubmit ? (
          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            {body}
          </form>
        ) : (
          body
        )}
      </div>
    </div>,
    document.body
  )
}

export default Modal
